import React, {
  useState,
  useEffect,
  KeyboardEvent,
  useRef,
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactMarkdown from 'react-markdown';

import { usePathname, useRouter } from 'next/navigation';

import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import remarkGfm from 'remark-gfm';

import { useUserInfo } from '@/context/UserInfoContext';
import { useVisibilityError } from '@/hooks/useVisibilityError';
import { DOC_VISIBILITY, DocVisibility } from '@/lib/api';
import {
  DisplayUserFollow,
  NewDocRequest,
  ProductPostType,
  PublicDocId,
  asPublicDocId,
} from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { getSubmitShortcutLabel, isApplePlatform } from '@/lib/utils';

import { InviteForm } from './InviteForm';
import { Modal } from './Modal';
import { VisibilityIcon, VisibilitySelector } from './Visibility';

interface ComposeBoxProps {
  onClose: () => void;
  topic: string;
  citationId?: PublicDocId | null;
  placeholder?: string;
  content?: string;
  onContentChange?: (content: string) => void;
  inheritedVisibility?: DocVisibility;
  className?: string;
  show_hints?: boolean;
  show_controls?: boolean;
  postType?: ProductPostType;
  minRows?: number;
}

/** Lets parent components (e.g. OmniSearch) trigger submission or focus programmatically. */
export interface ComposeBoxHandle {
  submit: () => Promise<void>;
  focus: () => void;
}

export const ComposeBox = forwardRef<ComposeBoxHandle, ComposeBoxProps>((props, ref) => {
  ComposeBox.displayName = 'ComposeBox';
  const composeLabelId = 'compose-label';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState<string>(props.content || '');
  const hasContent = content.trim().length > 0;
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [showUserSuggestions, setShowUserSuggestions] = useState<boolean>(false);
  const [userSuggestions, setUserSuggestions] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(0);
  const [visibility, setVisibility] = useState<DocVisibility>(
    props.inheritedVisibility || DOC_VISIBILITY.Public
  );
  const visibilityError = useVisibilityError(content, visibility, props.inheritedVisibility);
  const canPost = hasContent && !isPosting && !visibilityError;
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isFeedbackPost, setIsFeedbackPost] = useState(false);
  const [invalidMentions, setInvalidMentions] = useState<string[]>([]);
  const [submitShortcutLabel, setSubmitShortcutLabel] = useState('Ctrl+Enter or Cmd+Enter');
  const [authExpiredError, setAuthExpiredError] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { userInfo } = useUserInfo();

  useEffect(() => {
    if (props.content !== undefined) {
      setContent(props.content);
    }
  }, [props.content]);

  useEffect(() => {
    setSubmitShortcutLabel(getSubmitShortcutLabel());
  }, []);

  /** Drives the live preview toggle — only shown when the draft has markdown syntax. */
  const containsMarkdown = (text: string): boolean => {
    const markdownRegex =
      /(\*\*|__|\*|_|`|#|\[.*\]\(.*\)|!\[.*\]\(.*\)|\n\s*[-*+]\s|\n\s*\d+\.\s|\n\s*>\s)/;
    return markdownRegex.test(text);
  };

  useImperativeHandle(ref, () => ({
    submit: handlePost,
    focus: () => textareaRef.current?.focus(),
  }));

  /**
   * Submits the compose box content as a new document.
   * On 401, shows login-and-return guidance while preserving the user's draft.
   * Other failures surface a generic inline error so posting problems are never silent.
   */
  const handlePost = async () => {
    if (!canPost) {
      return;
    }

    setAuthExpiredError(false);
    setPostError(null);
    setIsPosting(true);
    try {
      const topic = props.topic;
      const requestBody: NewDocRequest = {
        content,
        topic,
        visibility,
      };

      if (props.postType) {
        requestBody.post_type = props.postType;
      }

      if (props.citationId) {
        requestBody.citation = props.citationId;
      }

      console.log('Posting content:', requestBody);

      const response = await fetch(`${API_URL}/doc/new`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        setAuthExpiredError(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to post content (${response.status})`);
      }

      const responseData = await response.json();
      console.log('New doc id:', responseData.doc_id);

      props.onClose();
      router.push(`/doc/${asPublicDocId(responseData.doc_id)}?processing_started=1`);
    } catch (error) {
      console.error('Error posting content:', error);
      setPostError("We couldn't post your message. Your draft is still here. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };

  const loginHref = `/login?redirect=${encodeURIComponent(pathname || '/')}`;

  const showPreview = containsMarkdown(content);

  /** Also tracks @mention state and suggestion visibility alongside draft sync. */
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    setCursorPosition(e.target.selectionStart || 0);

    if (props.onContentChange) {
      props.onContentChange(newContent);
    }

    // Check for @feedback tag
    const containsFeedback = /@feedback\b/i.test(newContent);
    setIsFeedbackPost(containsFeedback);

    // Check for invalid @mentions
    const completedMentionRegex = /@(\w+)(?=\s|[.!?,:;])/g;
    let completedMentions = Array.from(newContent.matchAll(completedMentionRegex), (m) => m[1]);

    const endMentionMatch = newContent.match(/@(\w+)$/);
    const selectionStart = e.target.selectionStart || 0;
    if (endMentionMatch && selectionStart <= newContent.length - endMentionMatch[0].length) {
      completedMentions.push(endMentionMatch[1]);
    }

    const validUsernames = userInfo?.followed_users?.map((f) => f.username) || [];
    const validMentions = new Set([...validUsernames, 'feedback']);
    const invalid = completedMentions.filter((mention) => !validMentions.has(mention));
    setInvalidMentions(invalid);

    const lastAtSymbolIndex = newContent.lastIndexOf('@', selectionStart);
    if (lastAtSymbolIndex !== -1 && selectionStart > lastAtSymbolIndex) {
      const query = newContent.slice(lastAtSymbolIndex + 1, selectionStart);

      if (query.includes(' ')) {
        setShowUserSuggestions(false);
        return;
      }

      const queryLower = query.toLowerCase();
      const userSuggestions =
        userInfo?.followed_users
          ?.filter((follow: DisplayUserFollow) =>
            follow.username.toLowerCase().startsWith(queryLower)
          )
          .map((follow: DisplayUserFollow) => follow.username) || [];

      const allSuggestions = [];
      if ('feedback'.startsWith(queryLower)) {
        allSuggestions.push('feedback');
      }
      // Deduplicate usernames in case of duplicates in followed_users
      const uniqueUserSuggestions = Array.from(new Set(userSuggestions));
      allSuggestions.push(...uniqueUserSuggestions);

      const showInviteOption = query.length === 0 && userInfo?.may_invite;

      if (allSuggestions.length > 0 || showInviteOption) {
        setUserSuggestions(allSuggestions);
        setShowUserSuggestions(true);

        if (query.length === 0 && showInviteOption) {
          setSelectedSuggestionIndex(-1);
        } else if (allSuggestions.length > 0) {
          setSelectedSuggestionIndex(0);
        } else {
          setSelectedSuggestionIndex(showInviteOption ? -1 : 0);
        }
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }
  };

  /**
   * Multiplexes keyboard input: arrow/enter/tab/escape navigate the @mention
   * dropdown when it's open, otherwise Ctrl+Enter (Cmd+Enter on Mac) submits.
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showUserSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (userInfo?.may_invite) {
          setSelectedSuggestionIndex((prevIndex) => {
            if (prevIndex === -1) {
              if (userSuggestions.length > 0) {
                const element = document.getElementById(`user-suggestion-0`);
                element?.scrollIntoView({ block: 'nearest' });
                return 0;
              }
              return -1;
            } else {
              const newIndex = Math.min(prevIndex + 1, userSuggestions.length - 1);
              const element = document.getElementById(`user-suggestion-${newIndex}`);
              element?.scrollIntoView({ block: 'nearest' });
              return newIndex;
            }
          });
        } else {
          setSelectedSuggestionIndex((prevIndex) => {
            const newIndex = Math.min(prevIndex + 1, userSuggestions.length - 1);
            const element = document.getElementById(`user-suggestion-${newIndex}`);
            element?.scrollIntoView({ block: 'nearest' });
            return newIndex;
          });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (userInfo?.may_invite) {
          setSelectedSuggestionIndex((prevIndex) => {
            if (prevIndex === 0) {
              const element = document.getElementById(`user-suggestion-invite`);
              element?.scrollIntoView({ block: 'nearest' });
              return -1;
            } else if (prevIndex === -1) {
              return -1;
            } else {
              const newIndex = Math.max(prevIndex - 1, 0);
              const element = document.getElementById(`user-suggestion-${newIndex}`);
              element?.scrollIntoView({ block: 'nearest' });
              return newIndex;
            }
          });
        } else {
          setSelectedSuggestionIndex((prevIndex) => {
            const newIndex = Math.max(prevIndex - 1, 0);
            const element = document.getElementById(`user-suggestion-${newIndex}`);
            element?.scrollIntoView({ block: 'nearest' });
            return newIndex;
          });
        }
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (selectedSuggestionIndex === -1 && userInfo?.may_invite) {
          setShowInviteModal(true);
          setShowUserSuggestions(false);
        } else if (userSuggestions.length > 0) {
          insertUsername(userSuggestions[selectedSuggestionIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowUserSuggestions(false);
      }
    } else if (e.key === 'Enter' && (isApplePlatform() ? e.metaKey : e.ctrlKey)) {
      e.preventDefault();
      if (!canPost) {
        return;
      }

      handlePost();
    }
  };

  /** Splices the selected username into the draft at the active @-query position. */
  const insertUsername = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const lastAtSymbolIndex = content.lastIndexOf('@', cursorPosition);
    const beforeAt = content.slice(0, lastAtSymbolIndex);
    const afterCursor = content.slice(cursorPosition);
    const newContent = `${beforeAt}@${username} ${afterCursor}`;
    setContent(newContent);
    setShowUserSuggestions(false);

    setTimeout(() => {
      const newCursorPosition = lastAtSymbolIndex + username.length + 2;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.focus();
    }, 0);
  };

  // Auto-focus the text area when component mounts
  useEffect(() => {
    // Small delay to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Close button when show_controls is true */}
      {props.show_controls && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton onClick={props.onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Feedback notice */}
      {isFeedbackPost && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Feedback posts are <strong>always visible</strong> to the General Wisdom team, but
            won&apos;t appear in your public profile unless you set visibility to Public.
          </Typography>
        </Alert>
      )}

      {/* Invalid mentions warning */}
      {invalidMentions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Warning: You mentioned @{invalidMentions.join(', @')} but you don&apos;t follow{' '}
            {invalidMentions.length > 1 ? 'these users' : 'this user'}. Only people you follow will
            be notified.
          </Typography>
        </Alert>
      )}

      {postError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">{postError}</Typography>
        </Alert>
      )}

      {authExpiredError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              component="a"
              href={loginHref}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              Log in
            </Button>
          }
        >
          <Typography variant="body2">
            Your session expired. Your draft is still here — log in, then return to this tab and
            click Post again.
          </Typography>
        </Alert>
      )}

      {/* Main compose area */}
      <Card elevation={1} sx={{ overflow: 'visible' }}>
        <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
          {props.show_controls !== false && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" component="h2">
                {props.citationId ? 'Reply' : 'New Post'}
              </Typography>
              <IconButton onClick={props.onClose} size="small" aria-label="Close compose box">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ position: 'relative' }}>
            <TextField
              inputRef={textareaRef}
              multiline
              fullWidth
              minRows={props.minRows || 3}
              maxRows={20}
              placeholder={props.placeholder || 'What are you thinking?'}
              value={content}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              disabled={isPosting}
              aria-labelledby={composeLabelId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1rem',
                  lineHeight: 1.5,
                },
              }}
            />

            {/* User suggestions dropdown */}
            {showUserSuggestions && (
              <Paper
                elevation={8}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                  zIndex: 1400, // Higher than dialogs (1300) and other UI elements
                  backgroundColor: 'background.paper', // Explicit background
                  opacity: 1, // Full opacity
                }}
              >
                <List dense>
                  {userInfo?.may_invite && (
                    <ListItemButton
                      id="user-suggestion-invite"
                      selected={selectedSuggestionIndex === -1}
                      onClick={() => {
                        setShowInviteModal(true);
                        setShowUserSuggestions(false);
                      }}
                    >
                      <ListItemIcon>
                        <PersonAddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Invite someone new..." />
                    </ListItemButton>
                  )}
                  {userSuggestions.map((username, index) => (
                    <ListItemButton
                      key={`${username}-${index}`}
                      id={`user-suggestion-${index}`}
                      selected={selectedSuggestionIndex === index}
                      onClick={() => insertUsername(username)}
                    >
                      <ListItemIcon>
                        {username === 'feedback' ? <CommentIcon /> : null}
                      </ListItemIcon>
                      <ListItemText primary={`@${username}`} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Markdown preview */}
          {showPreview && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview:
                </Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  {/* Using ReactMarkdown for live preview - instant feedback for users while typing.
                      Final document rendering uses backend-parsed structured content for consistency. */}
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                </Box>
              </Box>
            </>
          )}

          {/* Help text when typing */}
          {content.length > 0 && props.show_controls !== false && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Markdown is supported. Press {submitShortcutLabel} to post. Enter inserts a new line.
            </Typography>
          )}

          {/* Controls */}
          {props.show_controls !== false && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {visibilityError && (
                  <Alert severity="error" sx={{ py: 0, px: 1 }}>
                    <Typography variant="caption">{visibilityError}</Typography>
                  </Alert>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilitySelector
                  visibility={visibility}
                  setVisibility={setVisibility}
                  inheritedVisibility={props.inheritedVisibility}
                  showDropdown={showVisibilityDropdown}
                  setShowDropdown={setShowVisibilityDropdown}
                  position="above"
                />
                <Button
                  variant="contained"
                  onClick={handlePost}
                  disabled={!canPost}
                  title={`Post (${submitShortcutLabel})`}
                >
                  {isPosting ? 'Posting...' : 'Post'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Hints */}
      {props.show_hints && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Markdown supported:</strong> **bold**, *italic*, `code`, [links](url), etc.
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            <strong>@mentions:</strong> Type @ to mention followed users or invite new ones
          </Typography>
        </Box>
      )}

      {/* Invite modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite someone to General Wisdom"
      >
        <InviteForm
          onInviteCreated={(inviteCode, inviteUrl) => {
            // TODO(2): ENG-965 — Keep this modal open on invite success so the user can copy/share the
            // generated invite code/URL here; today it closes immediately, discarding them and preventing
            // any ComposeBox-local QR or copy flow.
            setShowInviteModal(false);
            // Since we don't get the invited user's username back,
            // we can't automatically insert it into the compose box
          }}
          onCancel={() => setShowInviteModal(false)}
        />
      </Modal>
    </Box>
  );
});

ComposeBox.displayName = 'ComposeBox';
